/* eslint-disable css-modules/no-undef-class */
import classNames from 'classnames';
import { AnimatePresence } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import Spinner from '@/components/atoms/spinner/spinner';
import Typography from '@/components/atoms/typography/typography';
import Video from '@/components/atoms/video/video';
import variables from '@/components/modules/inspirational-search-flow/inspirational-search-flow.variables';
import Container from '@/components/utilities/container/container';
import ModuleWrapper, {
  ModuleWrapperProps,
} from '@/components/utilities/module-wrapper/module-wrapper';
import { useI18n } from '@/libraries/contexts/i18n-context';
import InspirationalSearchFlowStep from './inspirational-search-flow-step/inspirational-search-flow-step';
import styles from './inspirational-search-flow.module.scss';

interface FormattedStep {
  $type: string;
  headline?: string;
  questions: {
    $type: string;
    headline: string;
    maxOptions: number;
    options: {
      $type: string;
      headline: string;
      id: number;
      image: Content.Properties.Image | null;
      subHeadline: string;
      themeIndex?: number;
    }[];
    subHeadline: string;
  }[];
  subHeadline: string;
}

export interface InspirationalSearchFlowProps extends ModuleWrapperProps {
  /** The text to show as a headline */
  headline?: Content.Components['InspirationalSearchFlowModuleDto']['headline'];
  /** The text to show as a pre headline */
  preHeadline?: Content.Components['InspirationalSearchFlowModuleDto']['preHeadline'];
  /** The link of the result page */
  resultPageLink?: Content.Components['InspirationalSearchFlowModuleDto']['resultPageLink'];
  /** The steps to show in the flow */
  steps?: Content.Components['InspirationalSearchFlowModuleDto']['steps'];
  /** The text to show as a sub headline */
  subHeadline?: Content.Components['InspirationalSearchFlowModuleDto']['subHeadline'];
}

const InspirationalSearchFlow = ({
  headline,
  preHeadline,
  resultPageLink,
  steps = [],
  subHeadline,
  ...rest
}: InspirationalSearchFlowProps) => {
  const { t } = useI18n();
  const [isLoading, setIsLoading] = useState(false);

  const formattedSteps: FormattedStep[] = useMemo(
    () =>
      [
        ...steps.map(step =>
          step.$type === 'QuestionStepDto'
            ? [step]
            : [
                {
                  $type: 'ThemeStepDto',
                  headline: '',
                  questions: [
                    {
                      $type: 'QuestionDto',
                      headline: step.themeHeadline,
                      maxOptions: step.maxThemes,
                      options: step.themes.map((theme, themeIndex) => ({
                        $type: 'OptionDto',
                        headline: theme.headline,
                        id: themeIndex,
                        image: theme.image,
                        subHeadline: theme.subHeadline,
                      })),
                      subHeadline: step.themeSubHeadline,
                    },
                  ],
                  subHeadline: '',
                },
                {
                  $type: 'QuestionStepDto',
                  headline: '',
                  questions: [
                    {
                      $type: 'QuestionDto',
                      headline: step.themeQuestionHeadline,
                      maxOptions: step.maxOptions,
                      options: step.themes
                        .map((theme, themeIndex) =>
                          theme.options.map(option => ({
                            ...option,
                            themeIndex,
                          })),
                        )
                        .flat(),
                      subHeadline: step.themeQuestionSubHeadline,
                    },
                  ],
                  subHeadline: '',
                },
              ],
        ),
      ].flat(),
    [steps],
  );

  const [currentStep, setCurrentStep] = useState(1);
  const [selections, setSelections] = useState<
    { $type: string; values: number[][] }[]
  >(
    formattedSteps.map(step => ({
      $type: step.$type,
      values: step.questions.map(() => []),
    })),
  );
  const [url, setUrl] = useState('');

  useEffect(() => {
    if (currentStep > 2 && resultPageLink) {
      setUrl(
        `${resultPageLink.url}?optionIds=${selections
          .filter(selection => selection.$type === 'QuestionStepDto')
          .map(selection => selection.values)
          .join(',')
          .replace(/,+$/, '')}`,
      );
    } else {
      setUrl('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selections]);

  const handleChange = (index: number, value: number[][]) => {
    setSelections(previousSteps =>
      previousSteps.map((previousStep, previousStepIndex) => {
        if (previousStepIndex === index) {
          return { ...previousStep, values: value };
        }

        return previousStep;
      }),
    );

    return selections;
  };

  const handleNext = () => {
    setCurrentStep(previousValue => previousValue + 1);
  };

  const handlePrevious = () => {
    if (currentStep === 1) return;

    setSelections(previousValue =>
      previousValue.map((step, stepIndex) => {
        if (stepIndex === currentStep - 1) {
          return {
            ...step,
            values: formattedSteps[stepIndex]?.questions.map(() => []) ?? [],
          };
        }

        return step;
      }),
    );

    setCurrentStep(previousValue => previousValue - 1);
  };

  const handleClickResult = () => {
    setIsLoading(true);
  };

  return (
    <ModuleWrapper
      {...rest}
      className={classNames(styles.component, {
        [styles.isLoading]: isLoading,
      })}
    >
      {(headline || preHeadline || subHeadline) && (
        <Container className={styles.headlineContainer}>
          {(headline || preHeadline) && (
            <div className={styles.headlineWrapper}>
              {preHeadline && (
                <Typography
                  className={styles.preHeadline}
                  tagStyle="headingMedium"
                >
                  {preHeadline}
                </Typography>
              )}

              {headline && (
                <Typography
                  className={styles.headline}
                  tagStyle="displayMedium"
                >
                  {headline}
                </Typography>
              )}
            </div>
          )}

          {subHeadline && (
            <Typography tagStyle="bodyLarge">{subHeadline}</Typography>
          )}
        </Container>
      )}

      <Container>
        <div className={styles.step}>
          {isLoading && (
            <Spinner
              className={styles.spinner}
              loadingText={t('items.inspirationalSearch.loadingText')}
            />
          )}

          <div className={styles.stepContent}>
            {variables.showBackground && (
              <div className={styles.stepVideoWrapper}>
                <Video
                  autoPlay
                  className={styles.stepVideo}
                  isPlaying
                  src="/assets/modules/inspirational-search-flow/background.webm"
                  type="video/webm"
                />
              </div>
            )}

            <AnimatePresence mode="wait">
              {formattedSteps.map(
                (step, stepIndex) =>
                  currentStep === stepIndex + 1 && (
                    <InspirationalSearchFlowStep
                      key={stepIndex}
                      currentStep={currentStep}
                      onChange={value => handleChange(stepIndex, value)}
                      onClickNext={handleNext}
                      onClickPrevious={stepIndex ? handlePrevious : undefined}
                      onClickResult={handleClickResult}
                      questions={
                        formattedSteps[stepIndex - 1]?.$type === 'ThemeStepDto'
                          ? step.questions.map(question => ({
                              ...question,
                              options: question.options.filter(
                                option =>
                                  typeof option.themeIndex === 'number' &&
                                  selections[
                                    stepIndex - 1
                                  ]?.values[0]?.includes(option.themeIndex),
                              ),
                            }))
                          : step.questions
                      }
                      resultPageUrl={url}
                      stepCount={formattedSteps.length}
                      value={selections[stepIndex].values ?? []}
                    />
                  ),
              )}
            </AnimatePresence>
          </div>
        </div>
      </Container>
    </ModuleWrapper>
  );
};

InspirationalSearchFlow.displayName = 'InspirationalSearchFlow';

export default InspirationalSearchFlow;
